import React from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import messagesCountQuery from '@aurora/shared-client/components/messages/MessagesCount.query.graphql';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import type { MessageConstraints } from '@aurora/shared-generated/types/graphql-schema-types';
import type {
  ContextNodeFragment,
  MessagesCountQuery,
  MessagesCountQueryVariables
} from '@aurora/shared-generated/types/graphql-types';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import useTranslation from '../../../useTranslation';
import localStyles from './IdeaMessagesStatusFilterDropdownItem.module.pcss';
import type { StatusItem } from './IdeaMessagesStatusFilterDropdown';

interface Props {
  /**
   * The idea status item to render
   */
  item: StatusItem;
  /**
   * The selected statuses
   */
  selectedStatuses: string[];
  /**
   * Callback function when a status filter is toggled.
   * @param item
   */
  onToggle: (item: StatusItem) => void;
  /**
   * The context node to scope the filter to.
   */
  contextNode: ContextNodeFragment;
}

/**
 * Single dropdown item for the idea status filter.
 * A query is run to find out how many idea messages are mapped to this status.
 * Only if the status has one or more messages, it will be displayed.
 *
 * @author rishabh.jivan
 */
const IdeaMessagesStatusFilterDropdownItem = ({
  item,
  selectedStatuses,
  onToggle,
  contextNode
}: Props): React.ReactElement => {
  const i18n = useTranslation(EndUserComponent.IDEA_MESSAGES_STATUS_FILTER);
  const cx = useClassNameMapper(localStyles);

  const constraints: MessageConstraints = {
    boardId: { eq: contextNode.id },
    statusKey: { eq: item.statusKey },
    depth: { eq: 0 }
  };
  const { data: messageCountResult, loading: statusLoading } = useQueryWithTracing<
    MessagesCountQuery,
    MessagesCountQueryVariables
  >(module, messagesCountQuery, {
    variables: {
      constraints
    },
    skip: !item.statusKey
  });

  if (item.statusKey && statusLoading) {
    return null;
  }

  const isDisabled: boolean = item.statusKey && messageCountResult?.messagesCount === 0;

  const checked =
    item.statusKey === ''
      ? selectedStatuses.length === 0 || selectedStatuses.includes('')
      : selectedStatuses.includes(item.statusKey);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isDisabled && event.key === 'Enter') {
      onToggle(item);
    }
  };

  return (
    <div
      key={item.statusKey}
      title={isDisabled ? i18n.formatMessage('disabledTootipText') : null}
      className={cx('lia-dropdown-item lia-g-my-10 lia-g-mx-20', {
        'lia-disabled-item': isDisabled
      })}
      role="button"
      tabIndex={0}
      onClick={event => {
        event.preventDefault();
        event.stopPropagation();
        if (!isDisabled) {
          onToggle(item);
        }
      }}
      onKeyPress={handleKeyPress}
      aria-disabled={isDisabled}
    >
      <input
        type="checkbox"
        readOnly
        checked={checked}
        className={cx('lia-g-mr-10')}
        aria-label={item.text}
        disabled={isDisabled}
      />
      {item.text}
    </div>
  );
};

export default IdeaMessagesStatusFilterDropdownItem;
